// AAL-86 MyServices Copilot: pure helpers of one conversation turn, shared by the page adapter.
// No Vue, no store, no HTTP.

import type { EntrySummary } from './form-contract'
import type { ReviewEntry } from './proposal-apply'
import type { ProposeResult } from './copilot-client'

const APPLIED = [ 'proposed', 'accepted' ]

/**
 * The system observation the page sends back once after an apply: the validation errors of the
 * entries that were written (proposed or accepted), one line per path. Null when nothing failed.
 */
export function validationObservation(entries: ReviewEntry[]): string | null {
	const failed = entries.filter(entry => APPLIED.includes(entry.status) && entry.errors.length)
	if (!failed.length) return null
	return [
		'Validation errors of the applied Proposal:',
		...failed.map(entry => `- ${entry.path}: ${entry.errors.join('; ')}`),
	].join('\n')
}

/** Entry objects of the page (ticket types, tax classes, categories, tags, ...) as contract choices. */
export function summarizeEntries(entries: any[] | undefined | null, locale: string): EntrySummary[] {
	const summaries: EntrySummary[] = []
	for (const entry of entries ?? []) {
		const id = entry?.sys?.id
		if (typeof id !== 'string' || !id) continue
		const title = localized(entry.fields?.title, locale) ?? localized(entry.fields?.name, locale) ?? localized(entry.name, locale)
		summaries.push(title === undefined ? { id } : { id, title })
	}
	return summaries
}

function localized(value: unknown, locale: string): string | undefined {
	if (typeof value === 'string') return value
	if (!value || typeof value !== 'object') return undefined
	const texts = value as Record<string, unknown>
	const text = texts[locale] ?? Object.values(texts).find(v => typeof v === 'string' && v)
	return typeof text === 'string' ? text : undefined
}

export type StopKind = 'complete' | 'budget' | 'rejections' | 'degraded' | 'error'

export type RunOutcome = {
	/** why the run stopped, one chat message each (ticket 16) */
	kind: StopKind
	/** the bare translation key of the chat message for this stop; null for a complete run */
	messageKey: string | null
	/** the budget that ran out (`tool budget`, `token budget`, `timeout`) or the model error; null otherwise */
	detail: string | null
	/** the run ended without finishing: the Proposal holds what was accepted until then */
	partial: boolean
	/** operations the service rejected against the contract */
	rejected: number
}

const BUDGETS = [ 'tool budget', 'token budget', 'timeout' ]
const MESSAGE_KEYS: Record<StopKind, string | null> = {
	complete: null,
	budget: 'copilotBudgetExhausted',
	rejections: 'copilotRunStoppedAfterRejections',
	degraded: 'copilotRunDegraded',
	error: 'copilotRunModelError',
}

/**
 * Maps the stop reason of the trajectory (`finished`, `finished after <why>`, `degraded: <why>`) to
 * its own outcome: a budget only for a budget stop, repeated rejections and
 * model errors each with their own message.
 */
export function runOutcome(result: Pick<ProposeResult, 'trajectory' | 'rejected_operations'>): RunOutcome {
	const reason = result.trajectory?.stop_reason ?? ''
	const degraded = /^degraded: (.+)$/.exec(reason)?.[1]
	const after = /^\w+ after (.+)$/.exec(reason)?.[1]
	const why = degraded ?? after ?? ''
	let kind: StopKind = 'complete'
	let detail: string | null = null
	if (BUDGETS.includes(why)) { kind = 'budget'; detail = why }
	else if (degraded?.startsWith('model error: ')) { kind = 'error'; detail = degraded.slice('model error: '.length) }
	else if (degraded === 'proposals rejected repeatedly') kind = 'rejections'
	else if (degraded !== undefined) kind = 'degraded'
	return { kind, messageKey: MESSAGE_KEYS[kind], detail, partial: degraded !== undefined, rejected: result.rejected_operations?.length ?? 0 }
}
